import { useLedgerApi } from '@givebacks/api/ledger';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QueryPagination, createQueryOptions } from 'core/Packages';

import { ledgerKeys } from './query-key-factory';
import { WalletEntriesSearch } from './search.types';
import { EntryByProviderResponse, WalletEntriesResponse } from './types';

// Ledger
interface ILedgerOwner {
  type: string;
  entry_privacy: string;
  donate_cashback: false;
  lifetime_cashback: number;
  lifetime_donations: number;
  lifetime_payouts: number;
  lifetime_transfers: number;
  current_transfers: number;
  yearly_goal: number;
  uuid: string;
  created_at: string;
  updated_at: string;
}

export function useLedgerOwner() {
  const LedgerApi = useLedgerApi();
  const lifetimeStats = async (): Promise<ILedgerOwner> => {
    const { data } = await LedgerApi.get('/owner');

    return data.owner;
  };
  return useQuery({
    queryKey: ['ledger', 'owner'],
    queryFn: lifetimeStats,
  });
}

export function useGetWalletActivity({
  pagination,
  queryOptions,
}: {
  pagination?: QueryPagination;
  queryOptions?: WalletEntriesSearch;
} = {}) {
  const LedgerApi = useLedgerApi();

  const { queryObject, queryPagination } = createQueryOptions<WalletEntriesSearch>({
    search: queryOptions,
    pagination,
  });

  const walletActivity = async ({
    pageParam,
  }: {
    pageParam: { limit: number; offset: number };
  }) => {
    const { queryString } = createQueryOptions<WalletEntriesSearch>({
      search: queryOptions,
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
    });
    const { data } = await LedgerApi.get<WalletEntriesResponse>(
      `/entries?order[timestamp]=desc${queryString}`
    );
    return data;
  };

  const key = ledgerKeys.entries.all;

  return useInfiniteQuery({
    queryKey: [...key, queryObject, queryPagination],
    queryFn: walletActivity,
    initialPageParam: { limit: 10, offset: 0 },
    getNextPageParam: (lastPageData) => {
      const { meta } = lastPageData;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
    placeholderData: keepPreviousData,
  });
}

// Mutations 👇

// React Query: doc: Optimistically update
// This is the extremely long version of a mutation update. This is not the recommended route for 99% of things, I have done this to demonstrate
// some of the impressive functionality
export function useUpdateLedgerOwner() {
  const queryClient = useQueryClient();
  const LedgerApi = useLedgerApi();

  const updateOwner = async (request: { [type: string]: string | boolean }) => {
    const { data } = await LedgerApi.put('/owner', { owner: { ...request } });
    return data;
  };
  return useMutation({
    mutationFn: updateOwner,

    // When mutate is called:
    onMutate: async (request) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['ledger', 'owner'],
      });

      // Snapshot the previous value
      const previousLedger = queryClient.getQueryData(['ledger', 'owner']);

      // Optimistically update to the new value
      queryClient.setQueryData(['ledger', 'owner'], (prev: any) => {
        return { ...prev, ...request };
      });

      // Return a context object with the snapshotted value
      return { previousLedger };
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, _status, context) => {
      queryClient.setQueryData(['ledger', 'owner'], context?.previousLedger);
    },

    // Always refetch after error or success:
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['ledger', 'owner'],
      });
    },
  });
}

export const useShowEntryByProviderId = (providerId?: string) => {
  const LedgerApi = useLedgerApi();

  const getEntry = async () => {
    const { data } = await LedgerApi.get<EntryByProviderResponse>(`/entries/${providerId}`);
    return data.entry;
  };

  const key = providerId ? ledgerKeys.entries.id(providerId).detail : [];
  return useQuery({
    queryFn: getEntry,
    queryKey: key,
  });
};
